import { useState, useRef, useCallback, useEffect } from 'react'
import { apiFetch } from '../lib/apiFetch'

interface UseMeetingNotesOpts {
  roomId: string | undefined
  enabled: boolean
}

export function useMeetingNotes({ roomId, enabled }: UseMeetingNotesOpts) {
  const [notes, setNotes] = useState('')
  const [summary, setSummary] = useState('')
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const dirtyRef = useRef(false)
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Load notes + summary on mount
  useEffect(() => {
    if (!roomId || !enabled || loaded) return
    const load = async () => {
      try {
        const [notesRes, summaryRes] = await Promise.all([
          apiFetch(`/api/visio/rooms/${roomId}/notes`),
          apiFetch(`/api/visio/rooms/${roomId}/summary`),
        ])
        if (notesRes.ok) {
          const data = await notesRes.json()
          setNotes(data.text || '')
        }
        if (summaryRes.ok) {
          const data = await summaryRes.json()
          setSummary(data.summary || '')
        }
      } catch {}
      setLoaded(true)
    }
    load()
  }, [roomId, enabled, loaded])

  // Save notes to backend
  const saveNotes = useCallback(async () => {
    if (!roomId || !dirtyRef.current) return
    dirtyRef.current = false
    setSaving(true)
    try {
      await apiFetch(`/api/visio/rooms/${roomId}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: notes }),
      })
    } catch {}
    setSaving(false)
  }, [roomId, notes])

  // Auto-save every 30s
  useEffect(() => {
    if (!enabled || !roomId) return
    autoSaveRef.current = setInterval(() => {
      if (dirtyRef.current) saveNotes()
    }, 30_000)
    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current)
      // Save on unmount
      if (dirtyRef.current) saveNotes()
    }
  }, [enabled, roomId, saveNotes])

  const updateNotes = useCallback((text: string) => {
    setNotes(text)
    dirtyRef.current = true
  }, [])

  const generateSummary = useCallback(async () => {
    if (!roomId || generating) return
    setGenerating(true)
    try {
      const res = await apiFetch(`/api/visio/rooms/${roomId}/summary`, {
        method: 'POST',
      })
      if (res.ok) {
        const data = await res.json()
        setSummary(data.summary || '')
      } else {
        const err = await res.json().catch(() => ({ detail: 'Erreur' }))
        setSummary(`Erreur: ${err.detail || 'Impossible de generer le resume'}`)
      }
    } catch {
      setSummary('Erreur: impossible de contacter le serveur')
    }
    setGenerating(false)
  }, [roomId, generating])

  return {
    notes,
    updateNotes,
    summary,
    generating,
    saving,
    saveNotes,
    generateSummary,
  }
}
